import type { ChatMessage } from "~/components/AiChat";

export const normalizeProjectId = (id: string | number | null | undefined): string => {
  if (id === null || id === undefined || id === "") return "";
  return String(id);
};

export const projectIdsMatch = (
  a: string | number | null | undefined,
  b: string | number | null | undefined,
): boolean => {
  const left = normalizeProjectId(a);
  const right = normalizeProjectId(b);
  if (!left || !right) return false;
  return Number(left) === Number(right);
};

export const messageDedupeKey = (message: Pick<ChatMessage, "role" | "content">): string =>
  `${message.role}|${String(message.content ?? "").trim()}`;

/** Merge backend history into in-memory messages without dropping WS-only entries. */
export const mergeChatMessages = (
  existing: ChatMessage[],
  incoming: ChatMessage[],
): ChatMessage[] => {
  if (incoming.length === 0) return existing;
  if (existing.length === 0) return [...incoming];

  const keys = new Set(existing.map(messageDedupeKey));
  const toAppend = incoming.filter((m) => !keys.has(messageDedupeKey(m)));
  if (toAppend.length === 0) return existing;
  return [...existing, ...toAppend];
};

export type ActiveChatSessionFlags = {
  isWaitingAnswer?: boolean;
  isGeneratingCode?: boolean;
  pendingCodeGeneration?: boolean;
  invokedTool?: string;
};

export const hasActiveChatSession = (flags: ActiveChatSessionFlags): boolean =>
  Boolean(
    flags.isWaitingAnswer ||
      flags.isGeneratingCode ||
      flags.pendingCodeGeneration ||
      (flags.invokedTool && flags.invokedTool !== ""),
  );

/** Progress bar + redirect apply only on the first codegen (chat → Sherpa Studio). */
export const isChatToStudioFirstGeneration = (hasGeneratedCode: boolean): boolean =>
  !hasGeneratedCode;

/** Normalize WS progress to 0–100 for UI (handles 0–1 fractions and out-of-range values). */
export const normalizeProgressPercent = (raw: unknown): number => {
  const value = Number(raw);
  if (!Number.isFinite(value)) return 0;
  const clamped = Math.min(100, Math.max(0, value));
  if (clamped > 0 && clamped <= 1) return Math.round(clamped * 100);
  return Math.round(clamped);
};

/** Extract chat/project id from common API response shapes. */
export const extractChatIdFromResponse = (data: unknown): string | null => {
  if (!data || typeof data !== "object") return null;
  const record = data as Record<string, unknown>;
  const candidates = [
    record.chatId,
    record.historyId,
    record.projectId,
    (record.returnedObject as Record<string, unknown> | undefined)?.chatId,
    (record.returnedObject as Record<string, unknown> | undefined)?.historyId,
    (record.returnedObject as Record<string, unknown> | undefined)?.projectId,
  ];
  for (const value of candidates) {
    if (value !== null && value !== undefined && value !== "") {
      return normalizeProjectId(value as string | number);
    }
  }
  return null;
};

export type ProjectIdContext = {
  historyId: string;
  studioProjectId: string | null;
  incomingId: string | number | null | undefined;
};

/**
 * Returns true when a websocket payload should be applied to the current session.
 * Accepts messages when historyId is unset, or when incoming id matches store or studio route id.
 */
/** Normalize websocket or API payloads into store chat lines. */
export const normalizeChatMessage = (
  raw: unknown,
  fallbackRole: "user" | "assistant" = "assistant",
): ChatMessage => {
  if (!raw) {
    return { role: fallbackRole, content: "" };
  }
  if (typeof raw === "string") {
    return { role: fallbackRole, content: raw };
  }
  if (typeof raw === "object") {
    const record = raw as Record<string, unknown>;
    const role =
      record.role === "user" || record.role === "assistant"
        ? record.role
        : fallbackRole;
    const content =
      typeof record.content === "string"
        ? record.content
        : record.content != null
          ? JSON.stringify(record.content)
          : "";
    return { role, content };
  }
  return { role: fallbackRole, content: String(raw) };
};

export const isWsProjectIdPertinent = ({
  historyId,
  studioProjectId,
  incomingId,
}: ProjectIdContext): boolean => {
  const incoming = normalizeProjectId(incomingId as string | number);
  if (!incoming) return true;

  const activeHistory = normalizeProjectId(historyId);
  if (!activeHistory) return true;

  if (projectIdsMatch(incoming, activeHistory)) return true;

  const routeProject = normalizeProjectId(studioProjectId ?? "");
  if (routeProject && projectIdsMatch(incoming, routeProject)) {
    return true;
  }

  return false;
};
